import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OkResponse, RecoveryCodesResponse } from '@cloudsign/shared';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { CAPABILITIES_KEY, useCaps } from '../lib/session';
import { ErrorLine, fieldErrors } from '../components/ErrorPanel';
import { RecoveryCodes, TotpEnrol } from '../components/TotpEnrol';
import { Button, Card, PageHeader, TextField } from '../components/ui';
import { useToastOnSuccess } from '../components/Toast';
import { ConnectedAppsCard } from '../modules/security/ConnectedApps';

function ProfileCard() {
  const caps = useCaps();
  const qc = useQueryClient();
  const [fullName, setFullName] = useState(caps.account.fullName);
  const save = useMutation({
    mutationFn: () => api.patch('/me', { body: { fullName }, schema: OkResponse }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CAPABILITIES_KEY }),
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card title={t('account.profile')}>
      <form onSubmit={submit} className="max-w-md space-y-4">
        <ErrorLine error={save.error} />
                <TextField label={t('auth.email')} value={caps.account.email} readOnly />
        <TextField label={t('auth.fullName')} required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <div className="flex justify-end"><Button type="submit" variant="primary" disabled={save.isPending}>
          {t('common.save')}
        </Button></div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/auth/password/change', { body: { currentPassword: current, newPassword: next }, schema: OkResponse }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
    },
  });
  useToastOnSuccess(change.isSuccess, t('account.passwordChanged'));
  const errs = fieldErrors(change.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    change.mutate();
  };
  return (
    <Card title={t('auth.changePassword')}>
      <form onSubmit={submit} className="max-w-md space-y-4">
        <ErrorLine error={change.error} />
                <TextField
          label={t('auth.currentPassword')}
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          error={errs['currentPassword']}
        />
        <TextField
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          minLength={8}
          required
          value={next}
          onChange={(e) => setNext(e.target.value)}
          error={errs['newPassword']}
        />
        <div className="flex justify-end"><Button type="submit" variant="primary" disabled={change.isPending}>
          {t('auth.changePassword')}
        </Button></div>
      </form>
    </Card>
  );
}

function TwoFactorCard() {
  const caps = useCaps();
  const qc = useQueryClient();
  const [enrolling, setEnrolling] = useState(false);
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'off' | 'codes' | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });

  const disable = useMutation({
    mutationFn: () => api.post('/auth/totp/disable', { body: { password }, schema: OkResponse }),
    onSuccess: async () => {
      setMode(null);
      setPassword('');
      await refresh();
    },
  });
  const regenerate = useMutation({
    mutationFn: () => api.post('/auth/recovery/regenerate', { body: { password }, schema: RecoveryCodesResponse }),
    onSuccess: (r) => {
      setMode(null);
      setPassword('');
      setCodes(r.codes);
    },
  });

  const on = caps.account.twoFactorEnabled;
  if (enrolling) {
    return (
      <Card title={t('auth.totpSetupTitle')}>
        <TotpEnrol
          onDone={() => {
            setEnrolling(false);
            void refresh();
          }}
          onCancel={() => setEnrolling(false)}
        />
      </Card>
    );
  }
  if (codes) {
    return (
      <Card>
        <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
      </Card>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'off') disable.mutate();
    else if (mode === 'codes') regenerate.mutate();
  };
  return (
    <Card title={t('auth.totpTitle')}>
      <p className="mb-4">{on ? t('auth.twoFactorOn') : t('auth.twoFactorOff')}</p>
      {mode ? (
        <form onSubmit={submit} className="max-w-md space-y-4">
          <ErrorLine error={disable.error ?? regenerate.error} />
          <TextField
            label={t('auth.currentPassword')}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="flex gap-2">
            <Button type="submit" variant={mode === 'off' ? 'danger' : 'primary'} disabled={disable.isPending || regenerate.isPending}>
              {mode === 'off' ? t('auth.turnOff2fa') : t('auth.regenerateCodes')}
            </Button>
            <Button
              onClick={() => {
                setMode(null);
                setPassword('');
                disable.reset();
                regenerate.reset();
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : on ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setMode('codes')}>{t('auth.regenerateCodes')}</Button>
          <Button variant="danger" onClick={() => setMode('off')}>
            {t('auth.turnOff2fa')}
          </Button>
        </div>
      ) : (
        <Button variant="primary" onClick={() => setEnrolling(true)}>
          {t('auth.turnOn2fa')}
        </Button>
      )}
    </Card>
  );
}

export function AccountPage() {
  return (
    <>
      <PageHeader title={t('nav.account')} description={t('nav.account.desc')} />
      <div className="space-y-6">
        <ProfileCard />
        <PasswordCard />
        <TwoFactorCard />
        <ConnectedAppsCard />
      </div>
    </>
  );
}
